import { http } from '@/lib/apiClient';
import type {
  ConvertiLeadInput,
  ConvertiLeadResult,
  Lead,
  LeadCalendarioEvent,
  LeadDetail,
  LeadFilters,
  LeadMessaggio,
  LeadImportConfirm,
  LeadImportPreview,
  LeadInput,
  LeadMutationResult,
  LeadPreventivo,
  LeadStato,
  Paginated,
  UUID,
} from '@/types';

const BASE = '/admin/lead';

export const leadService = {
  list(filters: LeadFilters = {}): Promise<Paginated<Lead>> {
    return http.get<Paginated<Lead>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<LeadDetail> {
    return http.get<LeadDetail>(`${BASE}/${id}`);
  },

  create(input: LeadInput): Promise<LeadMutationResult> {
    return http.post<LeadMutationResult>(BASE, input);
  },

  update(id: UUID, input: LeadInput): Promise<LeadMutationResult> {
    return http.patch<LeadMutationResult>(`${BASE}/${id}`, input);
  },

  changeStato(
    id: UUID,
    input: {
      stato: LeadStato;
      richiamo_at?: string;
      appuntamento_at?: string;
      partecipanti_ids?: UUID[];
      invita_referente?: boolean;
    },
  ): Promise<LeadDetail> {
    return http.patch<LeadDetail>(`${BASE}/${id}/stato`, input);
  },

  messaggi(id: UUID): Promise<LeadMessaggio[]> {
    return http.get<LeadMessaggio[]>(`${BASE}/${id}/messaggi`);
  },

  addMessaggio(id: UUID, corpo: string): Promise<LeadMessaggio> {
    return http.post<LeadMessaggio>(`${BASE}/${id}/messaggi`, { corpo });
  },

  calendario(params: { from: string; to: string; utente_id?: UUID }): Promise<{ data: LeadCalendarioEvent[] }> {
    return http.get<{ data: LeadCalendarioEvent[] }>(`${BASE}/calendario`, { params });
  },

  createAppuntamento(input: {
    inizia_at: string;
    durata_minuti?: number;
    titolo?: string;
    lead_id?: UUID | null;
    cliente_id?: UUID | null;
    partecipanti_ids: UUID[];
    invita_referente?: boolean;
  }): Promise<{ id: UUID }> {
    return http.post<{ id: UUID }>('/admin/appuntamenti', input);
  },

  updateAppuntamento(
    id: UUID,
    input: {
      inizia_at: string;
      durata_minuti: number;
      titolo: string;
      partecipanti_ids: UUID[];
      invita_referente?: boolean;
    },
  ): Promise<{ id: UUID }> {
    return http.patch<{ id: UUID }>(`/admin/appuntamenti/${id}`, input);
  },

  spostaRichiamo(id: UUID, richiamo_at: string): Promise<{ id: UUID }> {
    return http.patch<{ id: UUID }>(`${BASE}/${id}/richiamo`, { richiamo_at });
  },

  annullaAppuntamento(id: UUID): Promise<{ id: UUID }> {
    return http.post<{ id: UUID }>(`/admin/appuntamenti/${id}/annulla`);
  },

  converti(id: UUID, input: ConvertiLeadInput): Promise<ConvertiLeadResult> {
    return http.post<ConvertiLeadResult>(`${BASE}/${id}/converti`, input);
  },

  listPreventivi(id: UUID): Promise<LeadPreventivo[]> {
    return http.get<LeadPreventivo[]>(`${BASE}/${id}/preventivi`);
  },

  template(): Promise<Blob> {
    return http.get<Blob>(`${BASE}/import/template`, { responseType: 'blob' });
  },

  previewImport(file: File): Promise<LeadImportPreview> {
    const form = new FormData();
    form.append('file', file);
    return http.post<LeadImportPreview, FormData>(`${BASE}/import/preview`, form, {
      headers: { 'Content-Type': undefined },
    });
  },

  confirmImport(importId: UUID): Promise<LeadImportConfirm> {
    return http.post<LeadImportConfirm>(`${BASE}/import/confirm`, { import_id: importId });
  },

  report(importId: UUID): Promise<Blob> {
    return http.get<Blob>(`${BASE}/import/${importId}/report.xlsx`, { responseType: 'blob' });
  },
};
